// ============================================================
// DetailChrome 상태별 표시 해소 (순수 함수, React 무관)
// ============================================================
//
// template.detailChrome(DetailChromeConfig)를 report.status(coarse 4값) 축으로 해소해
// headerLeft/headerRight/actionBar 3슬롯의 표시 항목(노출/슬롯/순서/라벨)을 산출한다.
// **동작(behavior)은 코드 소유** — 여기선 배치/표시 결정만. mandatory 액션은 hidden 무시(강제 노출).
//
// 이 함수가 web(useComposedReportActions→resolveDetailChromeActions)과 admin(DetailChromeEditor
// WYSIWYG 미리보기) 양쪽의 **단일 배치 결정 소스**다 — 미리보기가 실제 상세 렌더와 갈리지 않게 한다.
// - web: tagged(런타임 권한/substate 게이팅된 실제 액션) ∩ 이 레이아웃 → StickyHeaderAction 배치.
// - admin: 상태 eligible 전체를 이 레이아웃으로 프레임에 렌더(런타임 게이팅 전 "가능한 최대 집합").
// 결정: docs/decisions/2026-07-21-compose-chrome-per-state-detail
// 미러 참고(축=뷰): composeChromeResolve.ts

import {
  DETAIL_CHROME_ACTIONS,
  DETAIL_CHROME_ACTION_LABELS,
  DETAIL_CHROME_CAPABILITY_AXES,
} from '@safetyhub/shared';
import type {
  DetailChromeActionId,
  DetailChromeConfig,
  DetailChromeOverflow,
  DetailChromeSlot,
  DetailChromeStatus,
} from '@safetyhub/shared';
import type { RenderData } from './types';
import { evaluateCondition } from './RenderTemplate';

/** 해소된 배치 항목 1개(표시 결정만 — 실제 동작/핸들러는 소비자가 붙인다). */
export interface ResolvedDetailChromeLayoutItem {
  actionId: DetailChromeActionId;
  slot: DetailChromeSlot;
  order: number;
  /** 숨김 불가(라이프사이클 필수) — hidden 오버라이드 무시. */
  mandatory: boolean;
  /** config override 라벨(ov?.label ?? item?.label). 없으면 undefined — 소비자가 기본 라벨 결정. */
  labelOverride?: string;
  /** 해소된 오버플로우 정책 — mandatory는 'never'로 강제, 그 외 item.overflow ?? 'auto'. */
  overflow: DetailChromeOverflow;
  /** 범용 조건(enableWhen) 평가 결과 — scope 주어질 때만 설정. false=비활성. 미설정(undefined)=활성. */
  enabled?: boolean;
  /** enabled=false일 때 사유(item.disabledReason). */
  disabledReason?: string;
  /** 강조(primary) 여부 — primaryWhen(scope) 평가 또는 정적 item.primary. 미설정=소비자 기본(mandatory 등). */
  primary?: boolean;
}

/** 슬롯별 해소 결과 — 3키 모두 존재(빈 배열이라도), 각 슬롯은 order 오름차순. */
export type DetailChromeLayout = Record<DetailChromeSlot, ResolvedDetailChromeLayoutItem[]>;

const EMPTY_LAYOUT = (): DetailChromeLayout => ({
  headerLeft: [],
  headerRight: [],
  actionBar: [],
});

/**
 * 단일 actionId의 상태별 **배치 결정**(노출/슬롯/순서/라벨override) — web/admin 공용 코어.
 * optional 항목이 이 상태에서 hidden 오버라이드면 null(mandatory는 hidden 무시 → 항상 non-null).
 *
 * ⚠️ eligibility(레지스트리 statuses) 게이팅은 하지 **않는다** — 그건 호출자의 책임이다.
 *   - web: 코드가 상태/권한으로 이미 게이팅한 tagged 액션에만 호출(재게이팅 불필요·금지 — 기존 계약).
 *   - admin: resolveDetailChromeLayout이 statuses 필터 후 호출.
 *
 * @param config 템플릿 detailChrome(미지정이면 레지스트리 기본 배치).
 * @param status 현재 report.status(coarse 4값) — statusOverrides 매칭 축.
 * @param actionId 배치를 구할 내장 액션 id.
 * @param scope 범용 조건(visibleWhen/enableWhen/primaryWhen) 평가용 chrome-scope 변수값(RenderData).
 *              주어지면 visibleWhen 미충족 항목은 null, enableWhen으로 enabled/disabledReason 산출,
 *              primaryWhen/item.primary/defaultPrimary로 primary 산출. 미지정=조건 무시(하위호환).
 */
export function resolveDetailChromePlacement(
  config: DetailChromeConfig | undefined,
  status: DetailChromeStatus,
  actionId: DetailChromeActionId,
  scope?: RenderData,
): ResolvedDetailChromeLayoutItem | null {
  const meta = DETAIL_CHROME_ACTIONS[actionId];
  // 미등록/알 수 없는 actionId(버전 skew·오타 등)는 크래시 대신 미배치 — 한 액션이 상세 전체를
  // 무너뜨리지 않게 그레이스풀 처리. 정상 경로(레지스트리 등록 액션)에선 도달하지 않는다.
  if (!meta) return null;

  const item = config?.items.find((i) => i.actionId === actionId);
  const ov = item?.statusOverrides?.[status];

  // mandatory는 hidden 무시(강제 노출). optional만 숨김 적용.
  if (ov?.hidden && !meta.mandatory) return null;

  const placement: ResolvedDetailChromeLayoutItem = {
    actionId,
    slot: ov?.slot ?? item?.slot ?? meta.defaultSlot,
    order: ov?.order ?? item?.order ?? meta.defaultOrder,
    mandatory: meta.mandatory,
    labelOverride: ov?.label ?? item?.label,
    // mandatory는 항상 노출(never 강제), 그 외 item.overflow ?? 액션 기본(defaultOverflow) ?? auto.
    overflow: meta.mandatory ? 'never' : (item?.overflow ?? meta.defaultOverflow ?? 'auto'),
  };

  // 범용 조건(scope 주어질 때만) — visibleWhen 게이팅 + enableWhen 활성 + primaryWhen 강조
  // (evaluateCondition 단일 소스). scope 미지정 시 조건 무시(하위호환 — enabled/primary undefined).
  if (scope) {
    if (item?.visibleWhen && !evaluateCondition(item.visibleWhen, scope)) return null; // 미노출
    if (item?.enableWhen) {
      placement.enabled = evaluateCondition(item.enableWhen, scope);
      if (!placement.enabled) placement.disabledReason = item.disabledReason;
    }
    // 강조 — primaryWhen 조건 우선, 없으면 정적 item.primary, 없으면 액션 기본(defaultPrimary).
    if (item?.primaryWhen) placement.primary = evaluateCondition(item.primaryWhen, scope);
    else if (item?.primary !== undefined) placement.primary = item.primary;
    else if (meta.defaultPrimary !== undefined) placement.primary = meta.defaultPrimary;
  }

  return placement;
}

/**
 * admin 미리보기 **시뮬레이션 컨텍스트** — 레지스트리 statuses 게이팅 위에 실제 web 런타임 게이팅
 * (useComposedReportActions)을 근사해 "이 상황에서 실제로 보이는" 버튼만 남긴다. 미지정이면 게이팅
 * 안 함(레지스트리 최대 집합 — 하위호환). 저작 패널(속성 편집)은 이 sim을 쓰지 않고 최대 집합을 본다.
 * - `hasApprovalLine=false`(완료형): 결재/취소/재제출/결재하기/이력/결재라인편집 없음 → DRAFT는 완료.
 * - `pendingPhase`: PENDING_APPROVAL 하위 지점. 'before'=결재 전(취소 가능), 'partial'=일부 결재(취소 불가).
 */
export interface DetailChromeSimContext {
  /** 결재라인 사용 여부(템플릿 approvalLineMeta 유무 시뮬레이션). false=완료형. */
  hasApprovalLine: boolean;
  /** PENDING_APPROVAL 진행 지점(결재중일 때만 의미). 미지정=before. */
  pendingPhase?: 'before' | 'partial';
  /**
   * 뷰어 역할 — 상세 페이지를 누가 보느냐. 미지정=최대 집합(작성자·결재자 액션 둘 다, 저작 관점).
   * - 'author'(작성자): 결재하기(결재자 액션) 숨김.
   * - 'approver'(결재자): 작성자 전용 액션(제출/취소/재제출/수정/결재라인편집/삭제/서명수집) 숨김.
   */
  viewerRole?: 'author' | 'approver';
  /**
   * 이 보고서가 **위임으로 만들어졌는가**(`report.delegatedContactId`). 미지정=false.
   *
   * 다른 축(`viewerRole`)과 달리 미지정을 "최대 집합"이 아니라 **false**로 읽는다. 위임은
   * 소수 경로라 미리보기 기본이 "위임 상태"면 저작자가 대부분의 템플릿에서 실제로는 뜨지 않을
   * 버튼을 보게 된다 — 근사가 오히려 오해를 만든다. 저작 패널의 최대 집합은 `sim` 자체를
   * 주지 않는 경로가 담당한다.
   *
   * ⚠️ 결재란 위젯의 `{type:'delegatedTo'}`와는 **다른 축**이다 — 그건 칸을 누가 채우나는
   * 저작 선언이고, 직접 작성이면 작성자로 폴백하므로 위임 여부를 가리지 못한다.
   */
  isDelegated?: boolean;
}

/**
 * 양식 자체의 능력(템플릿 메타) — 상태 축(`statuses`)·런타임 축(`DetailChromeSimContext`)과 **다른 축**이다.
 *
 * 런타임 축은 "지금 이 보고서가 어떤 상황인가"라 시뮬레이션(미리보기 바)에만 걸리지만, 능력 축은
 * "이 양식에서 그 버튼이 존재할 수 있는가"라 **최대 집합(sim 미지정)에도 걸린다** — 저작자에게
 * 도달 불가 버튼을 보여 주지 않기 위해서다.
 *
 * ⚠️ 미지정(`undefined`)은 게이팅하지 않는다(하위호환) — 능력을 모르는 호출자가 조용히 버튼을
 * 지워 버리는 쪽이 잡음보다 나쁘다. 판정할 수 있는 호출자는 반드시 값을 넘길 것.
 */
export interface DetailChromeTemplateCapabilities {
  /** 템플릿 `delegationMeta.enabled`(shared `isTemplateDelegationEnabled`). */
  delegationEnabled?: boolean;
  /**
   * 템플릿이 제출 후 흐름을 선언했는가 — `policy.postSubmit.flow`가 **등록된** 키일 때만 true.
   * (미등록 키는 web 소비처도 "흐름 없음"으로 읽으므로 여기서도 false여야 갈리지 않는다.)
   */
  postSubmitFlowDeclared?: boolean;
}

/**
 * 이 액션이 **양식 능력** 때문에 도달 불가인가 — 불가면 그 사유(출구 포함)를, 아니면 null.
 *
 * 판정은 shared `DETAIL_CHROME_CAPABILITY_AXES` 한 표에서 파생한다(액션별 손나열 없음).
 * ⚠️ 능력을 모르면(`capabilities` 미지정) 게이팅하지 않는다 — 능력을 모르는 호출자가 조용히
 * 버튼을 지워 버리는 쪽이 잡음보다 나쁘다(하위호환).
 *
 * admin 속성 패널이 같은 판정을 자기 파일에 복제하고 있었다 — 능력 축이 둘이 된 지금은 그
 * 복제가 곧 드리프트라, 판정을 여기 하나로 모으고 admin이 이것을 부른다.
 */
export function resolveDetailChromeUnsupportedReason(
  actionId: DetailChromeActionId,
  capabilities: DetailChromeTemplateCapabilities | undefined,
): string | null {
  if (!capabilities) return null;
  const meta = DETAIL_CHROME_ACTIONS[actionId];
  if (!meta) return null;
  const axis = DETAIL_CHROME_CAPABILITY_AXES.find(
    (a) => meta[a.requires] === true && capabilities[a.capability] !== true,
  );
  return axis ? axis.reason : null;
}

/** 작성자(또는 OWNER) 전용 액션 — 결재자 관점에선 노출 안 됨(열람/결재하기만). */
const AUTHOR_ONLY_ACTIONS: ReadonlySet<DetailChromeActionId> = new Set([
  'submit',
  'complete',
  'cancelSubmit',
  'resubmit',
  'edit',
  'approvalLineEdit',
  'delete',
  'contactSign',
  // 제출 후 흐름 재진입은 작성자(위임자)의 동사다 — 결재자 관점에서는 뜨지 않는다.
  // (web 훅도 같은 축으로 게이팅한다: 작성자 본인.)
  'postSubmitSetup',
  // ⚠️ 형제인 `postSubmitActivity`는 **여기 없다 — 의도다.** 설정은 위임자의 동사지만 활동
  // 화면은 아니다: 교육을 진행하는 사람이 작성자가 아닐 수 있고, 구 TBM 상세의 게이트도
  // 작성자가 아니라 **현장 배정**(`isAssigned`) 하나였다. web 훅도 같은 축으로 연다.
]);

/**
 * 시뮬레이션 게이트 — 실제 web 게이팅(useComposedReportActions)을 근사한다.
 * 라이프사이클/결재 관련 액션만 결재라인·진행지점으로 좁히고, edit/delete/pdf/share는 statuses로만.
 *
 * ⚠️ PENDING의 `cancelSubmit`은 단순 '취소'가 아니라 `결재 ▼` **드롭다운 번들**(결재/결재 취소/결재
 * 재전송)이다(web buildApprovalActions). 재전송은 일부 결재 후에도 가능하므로 드롭다운 자체는 두 phase
 * 모두 노출된다 — pendingPhase는 버튼 존재가 아니라 드롭다운 **안 '결재 취소' 활성 여부**만 바꾼다
 * (결재 전=취소 가능 / 일부 결재=취소 불가). 그 활성 차이는 버튼 단위 배치 밖이라 여기선 게이팅하지 않음.
 */
function passesSimGate(
  actionId: DetailChromeActionId,
  sim: DetailChromeSimContext,
  status: DetailChromeStatus,
): boolean {
  const { hasApprovalLine, viewerRole } = sim;
  // 뷰어 역할 게이팅(지정 시) — 작성자/결재자 관점 분리. 미지정=둘 다(최대 집합).
  if (viewerRole === 'approver' && AUTHOR_ONLY_ACTIONS.has(actionId)) return false;
  if (viewerRole === 'author' && actionId === 'approve') return false;
  switch (actionId) {
    // DRAFT 라이프사이클: 결재라인 있으면 '결재'(submit), 없으면 '완료'(complete) — 택일(둘 다 X).
    case 'submit':
      return hasApprovalLine;
    case 'complete':
      return !hasApprovalLine;
    // 결재 ▼ 드롭다운(취소/재전송)·결재하기·재제출·결재이력·결재라인 편집은 결재라인 있을 때만.
    // (cancelSubmit 드롭다운은 재전송 때문에 결재중 두 phase 모두 노출 — 위 주석 참조.)
    case 'cancelSubmit':
    case 'approve':
    case 'resubmit':
    case 'approvalHistory':
      return hasApprovalLine;
    case 'approvalLineEdit':
      // 결재중(PENDING_APPROVAL)의 결재라인 편집은 **대기(parked) 칸이 있을 때만** 열리는
      // 예외 진입이다(park-and-resume C2 — web은 서명행 `isApprovalSignatureParked`로 판정).
      // 대기 여부는 sim 축이 아니므로 미리보기에서는 숨긴다 — 보이게 두면 대부분의 결재중
      // 화면에 실제로는 없는 버튼을 저작자가 보게 된다(`isDelegated` 미지정=false와 같은 이유).
      // 최대 집합(sim 미지정) 경로에는 레지스트리 statuses대로 남는다.
      // ⚠️ 이 조건은 approvalLineEdit **단독** 케이스여야 한다 — 위 fall-through 묶음에 붙이면
      // 결재중 미리보기에서 결재▼/결재하기까지 통째로 사라진다(2026-08-28 merge 재검증에서 적발).
      return hasApprovalLine && status !== 'PENDING_APPROVAL';
    // 위임 사후 조작은 **위임으로 만든 보고서에만** — 직접 작성엔 되돌릴 대상 자체가 없다.
    // (web `useComposedReportActions`의 `isDelegated` 게이트와 같은 축.)
    case 'resendDelegation':
    case 'reassignDelegation':
    case 'releaseDelegation':
    case 'cancelDelegation':
      return !!sim.isDelegated;
    // 신규 지정은 **위임이 아닌** 보고서에만 — 위 넷과 정확히 반대 축이다(둘이 동시에 뜨는
    // 상황은 없다). `isDelegated` 미지정은 false로 읽히므로(위 필드 주석) 기본 미리보기에서는
    // 이 버튼이 보인다 — 저작자가 보는 대부분의 템플릿이 실제로 그 상태다.
    case 'assignDelegation':
      return !sim.isDelegated;
    // 제출 후 흐름 재진입 — **sim 축을 새로 두지 않는다.** 실제 web 게이트 셋 중 둘은 여기서
    // 이미 걸린다: 흐름 선언은 능력 축(`postSubmitFlowDeclared`)이고, 작성자는 위
    // AUTHOR_ONLY_ACTIONS다. 남은 하나("설정을 마쳤는가")를 축으로 두지 않는 것은 근사가
    // 느슨해서가 아니라 **상세에 도달한 선언 문서의 DRAFT는 항상 설정 완료이기 때문**이다 —
    // 설정 미완 DRAFT는 라우팅이 흐름 화면으로 보내 상세를 지나지 않는다(§5 표).
    case 'postSubmitSetup':
      return true;
    // 활동 화면 진입도 **sim 축을 새로 두지 않는다.** 흐름 선언은 능력 축
    // (`postSubmitFlowDeclared`)이 이미 걸고, 남은 web 게이트(현장 배정 · 폐기 아님)는 둘 다
    // *보고서 상황*이라 저작 미리보기가 흉내 낼 값이 아니다 — 미리보기가 재는 것은 「이 양식의
    // 이 상태에서 어떤 버튼이 놓이나」이고, 배정·폐기는 문서마다 갈리는 축이다.
    // ⚠️ 위 `postSubmitSetup`과 달리 `AUTHOR_ONLY_ACTIONS`에 없으므로 결재자 관점에서도 남는다
    // (그게 옳다 — 기록 열람은 결재자에게도 필요하다).
    case 'postSubmitActivity':
      return true;
    // 링크 재발송(F-27)도 **sim 축을 새로 두지 않는다** — 형제 둘과 같은 근거다. 흐름 선언은 능력
    // 축이 걸고, 남은 web 게이트(설정 완료 · 담당자 지정 · 발송 토큰 있음 · 현장 배정)는 전부
    // *보고서 상황*이라 저작 미리보기가 흉내 낼 값이 아니다. 활동 화면과 같이 작성자 전용이
    // 아니다(구 TBM 상세의 같은 버튼 게이트가 현장 배정 하나였다).
    case 'postSubmitResend':
      return true;
    // edit/delete/pdf/share는 결재라인 무관(레지스트리 statuses로만 게이팅).
    default:
      return true;
  }
}

/** `resolveDetailChromeLayout`의 선택 축 셋 — 각 축의 의미는 그 함수 JSDoc `@param opts`. */
export interface ResolveDetailChromeLayoutOptions {
  sim?: DetailChromeSimContext;
  scope?: RenderData;
  capabilities?: DetailChromeTemplateCapabilities;
}

/**
 * 상태에 노출 가능한 모든 내장 액션을 배치 해소해 슬롯별로 분류·정렬한다(admin WYSIWYG 미리보기용).
 * `sim` 미지정이면 런타임 게이팅 안 함 — "가능한 최대 집합"(저작 패널 의미론). `sim` 지정 시 실제 web
 * 게이팅(결재라인 유무·결재중 진행지점)을 근사해 "이 상황에서 실제로 보이는" 버튼만 남긴다(미리보기 바).
 *
 * @param config 템플릿 detailChrome(미지정이면 레지스트리 기본 배치).
 * @param status 현재 편집 중인 상태 탭(초안/결재중/완료/반려).
 * @param opts   선택 축 셋(옵션 객체 — 종전 위치 인자 3개는 2026-08-30 리뷰 W-quality13로 접었다.
 *               `undefined, undefined, caps` 같은 자리 맞추기가 호출부마다 반복됐다):
 *  - `sim`   미리보기 시뮬레이션 컨텍스트(결재라인 유무·진행지점). 미지정=최대 집합.
 *  - `scope` 범용 조건(visibleWhen/enableWhen) 평가용 chrome-scope 변수값(RenderData). 주어지면
 *            visibleWhen 미충족 항목 제외 + enableWhen으로 enabled 산출. 미지정=조건 무시(하위호환).
 *  - `capabilities` 양식 능력(위임 지원 여부 등). 주어지면 그 능력이 없는 액션은 **최대 집합에서도**
 *            제외 — 도달 불가 버튼을 저작자에게 보이지 않게. 미지정=능력 게이팅 없음(하위호환).
 */
export function resolveDetailChromeLayout(
  config: DetailChromeConfig | undefined,
  status: DetailChromeStatus,
  opts: ResolveDetailChromeLayoutOptions = {},
): DetailChromeLayout {
  const { sim, scope, capabilities } = opts;
  const buckets = EMPTY_LAYOUT();

  (Object.keys(DETAIL_CHROME_ACTIONS) as DetailChromeActionId[]).forEach((actionId) => {
    const meta = DETAIL_CHROME_ACTIONS[actionId];
    // admin eligibility 게이트 — 이 상태에 노출 가능한 액션만(레지스트리 statuses).
    if (!meta.statuses.includes(status)) return;
    // opt-in 액션(defaultHidden)은 저작자가 config에 명시적으로 추가했을 때만 배치(기본 미노출).
    if (meta.defaultHidden && !config?.items.some((i) => i.actionId === actionId)) return;
    // 양식 능력 게이트(capabilities 지정 시) — 그 능력이 없는 양식에서는 어떤 상태·어떤
    // 시뮬레이션에서도 도달 불가라 최대 집합에서도 뺀다(위임 미지원 양식의 위임 동사, 제출 후
    // 흐름 미선언 양식의 재진입). 능력을 모르면(미지정) 종전대로 남긴다.
    if (resolveDetailChromeUnsupportedReason(actionId, capabilities)) return;
    // 시뮬레이션 게이트(sim 지정 시) — 실제 web 게이팅 근사.
    if (sim && !passesSimGate(actionId, sim, status)) return;
    // 배치 + 범용 조건(scope 주어질 때만)은 공유 코어가 소유 — visibleWhen 미충족이면 null.
    const placement = resolveDetailChromePlacement(config, status, actionId, scope);
    if (!placement) return;
    buckets[placement.slot].push(placement);
  });

  (Object.keys(buckets) as DetailChromeSlot[]).forEach((slot) =>
    buckets[slot].sort((a, b) => a.order - b.order),
  );

  return buckets;
}

/** 슬롯 항목을 폭 기준으로 노출/메뉴로 분리한 결과. 순서는 입력 순서(정렬 완료본) 보존. */
export interface DetailChromeOverflowResult {
  /** 바에 그대로 노출할 항목(순서 보존). */
  visible: ResolvedDetailChromeLayoutItem[];
  /** '⋯' 메뉴로 접을 항목(순서 보존, 비면 메뉴 미표시). */
  menu: ResolvedDetailChromeLayoutItem[];
}

/**
 * 버튼 폭 추정(px) — 한글 라벨 기준 대략치(text-sm + px-3 좌우 패딩 + gap). DOM 측정 없이 fit 판정용.
 * 정확한 px가 아니라 상대적 fit 순서 판정이 목적(web·미리보기가 같은 추정 → parity by construction).
 */
export function estimateDetailChromeButtonWidth(label: string): number {
  return Math.ceil(label.length * 13) + 32;
}

/**
 * auto 항목을 available 폭에 맞게 fit 분리 — never(mandatory 포함)=항상 노출, always=항상 메뉴,
 * auto=**들어갈 만큼 노출하고 넘치는 것만 접는다**(임계 일괄접힘 아님). web 상세 하단바·미리보기 공용
 * (같은 추정 → 동작 일치). @returns 입력 순서 보존 index 배열.
 */
export function splitDetailChromeByFit(
  items: { label: string; overflow: DetailChromeOverflow }[],
  availableWidth: number,
): { visible: number[]; menu: number[] } {
  const MORE_WIDTH = 64; // '더보기 ⋯' 버튼 폭
  const BAR_PADDING = 24; // 바 좌우 여백
  let neverW = 0;
  let autoTotal = 0;
  let hasAlways = false;
  for (const it of items) {
    const w = estimateDetailChromeButtonWidth(it.label);
    if (it.overflow === 'never') neverW += w;
    else if (it.overflow === 'always') hasAlways = true;
    else autoTotal += w;
  }
  const budgetAll = availableWidth - BAR_PADDING;
  // 메뉴가 생기지 않는 경우(always 없고 전부 fit)엔 '더보기' 자리 예약 불필요.
  const fitsAll = !hasAlways && neverW + autoTotal <= budgetAll;
  const budget = budgetAll - neverW - (fitsAll ? 0 : MORE_WIDTH);
  const visible: number[] = [];
  const menu: number[] = [];
  let used = 0;
  items.forEach((it, i) => {
    if (it.overflow === 'never') {
      visible.push(i);
      return;
    }
    if (it.overflow === 'always') {
      menu.push(i);
      return;
    }
    const w = estimateDetailChromeButtonWidth(it.label);
    if (used + w <= budget) {
      visible.push(i);
      used += w;
    } else {
      menu.push(i);
    }
  });
  return { visible, menu };
}

/**
 * 한 슬롯의 해소 항목들을 뷰포트 폭 기준으로 노출/오버플로우 메뉴로 나눈다(fit 기반 — 넘치는 것만 접음).
 *
 * @param items         한 슬롯의 정렬된 해소 항목(resolveDetailChromeLayout[slot]).
 * @param viewportWidth 현재 가용 폭(px) — web=window.innerWidth, 팝아웃=활성 device 폭.
 */
export function resolveDetailChromeOverflow(
  items: ResolvedDetailChromeLayoutItem[],
  viewportWidth: number,
): DetailChromeOverflowResult {
  const shaped = items.map((it) => ({
    label: it.labelOverride ?? DETAIL_CHROME_ACTION_LABELS[it.actionId],
    overflow: it.overflow,
  }));
  const { visible, menu } = splitDetailChromeByFit(shaped, viewportWidth);
  return {
    visible: visible.map((i) => items[i]),
    menu: menu.map((i) => items[i]),
  };
}
